import { z } from "zod";

/**
 * Ancien formulaire à quatre champs. CONSERVÉ tel quel : c'est le contrat que
 * respectent les formulaires des sites clients qui postent sur
 * /api/public/contact (Bouillotte et les suivants). Le durcir casserait des
 * sites déployés qu'on ne redéploie pas en même temps que la plateforme.
 */
export const submitContactMessageSchema = z.object({
  name: z.string().min(1, "Nom requis").max(200),
  email: z.string().email("Email invalide").max(320),
  subject: z.string().min(1, "Sujet requis").max(300),
  message: z.string().min(1, "Message requis").max(5000),
  // Honeypot : PAS de max(0) — un champ rempli doit PASSER la validation pour
  // atteindre la branche « répondre OK sans rien écrire ». Avec max(0), le bot
  // recevait un 400 dont les fieldErrors lui désignaient le piège.
  website: z.string().optional(),
});

export type SubmitContactMessageInput = z.infer<typeof submitContactMessageSchema>;

/* ── Parcours de qualification (rlcore.fr) ─────────────────────────────── */

export const PROJECT_TYPES = [
  "vitrine",
  "ecommerce",
  "reservations",
  "unknown",
] as const;

export const TIMELINES = ["asap", "1-3m", "3-6m", "exploring"] as const;

export const BUDGETS = ["lt-1k", "1k-3k", "3k-5k", "gt-5k", "unknown"] as const;

/** Secteurs proposés — alignés sur les verticales métier de la plateforme. */
export const SECTORS = [
  "restaurant",
  "beaute",
  "commerce",
  "services",
  "other",
] as const;

export type ProjectType = (typeof PROJECT_TYPES)[number];
export type Timeline = (typeof TIMELINES)[number];
export type Budget = (typeof BUDGETS)[number];

/**
 * Demande envoyée depuis le parcours de rlcore.fr.
 *
 * SEULS le nom et l'e-mail sont exigés : chaque étape de qualification peut
 * être passée. C'est délibéré — la qualification sert à préparer la réponse,
 * pas à filtrer les prospects, et chaque champ obligatoire supplémentaire coûte
 * des demandes perdues.
 *
 * Les champs facultatifs restent de simples `.optional()` : les listes de choix
 * renvoient `undefined` quand rien n'est sélectionné, et les champs texte vides
 * arrivent en chaîne vide — normalisée en `null` à l'insertion (`blankToNull`).
 * Une normalisation par `z.preprocess` rendrait le type d'entrée opaque et
 * casserait l'inférence du formulaire.
 */
export const contactWizardSchema = z.object({
  name: z.string().min(1, "Nom requis").max(200),
  email: z.string().email("Email invalide").max(320),
  phone: z.string().max(40).optional(),
  projectType: z.enum(PROJECT_TYPES).optional(),
  sector: z.string().max(120).optional(),
  location: z.string().max(120).optional(),
  timeline: z.enum(TIMELINES).optional(),
  hasWebsite: z.boolean().optional(),
  budget: z.enum(BUDGETS).optional(),
  message: z.string().max(5000).optional(),
  locale: z.string().max(5).optional(),
  // Honeypot : PAS de max(0) — cf. submitContactMessageSchema ci-dessus.
  website: z.string().optional(),
});

/** Chaîne vide ou blanche → `null`, pour ne pas stocker de faux renseignements. */
export function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export type ContactWizardInput = z.infer<typeof contactWizardSchema>;

/**
 * Champs validés à chaque étape, dans l'ordre d'affichage.
 *
 * Consommé par `trigger(STEP_FIELDS[step])` côté formulaire : seule la dernière
 * étape porte des champs obligatoires, les précédentes se franchissent donc
 * toujours, y compris vides.
 */
export const STEP_FIELDS = [
  ["projectType"],
  ["sector", "location"],
  ["timeline", "hasWebsite"],
  ["budget"],
  ["name", "email", "phone", "message"],
] as const satisfies readonly (readonly (keyof ContactWizardInput)[])[];

export const WIZARD_STEP_COUNT = STEP_FIELDS.length;

export const updateContactMessageStatusSchema = z.object({
  status: z.enum(["new", "read", "archived"]),
});
